// 2026-09-22 신규 — 「이 화면처럼 캡처해 주세요」 안내 (대표 지시 2026-09-22: 네이버에서 어떤 화면을 찍어 보내야 하는지 직관적으로).
// 네이버 클립 크리에이터 앱의 「정산 완료 내역」 화면을 그대로 그려 보여 준다. 실제 캡처가 아니라 그림이며 금액은 ●●●,●●● 로 가린다.
// 문구·경로는 src/lib/naverRevenue.js CAPTURE_GUIDE 가 원본 — 여기서 새 문장을 만들지 않는다.
import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { CAPTURE_GUIDE, monthLabel, shiftMonth } from "../../lib/naverRevenue";
import { tt } from "../../i18n/tt";

/** 네이버 「정산 완료 내역」 화면 그림. highlightMonth = 우리가 묻는 발생월(YYYYMM) — 그 줄을 강조한다 */
function NaverSettlementSample({ highlightMonth }) {
  const y = highlightMonth ? highlightMonth.slice(0, 4) : "2026";
  const m = highlightMonth ? parseInt(highlightMonth.slice(4, 6), 10) : 8;
  // 2026-10-14 B안: 실제 표처럼 세 줄(두 달 전 · 한 달 전 · 그 달)을 보여 주고, 찾을 줄(큰 글씨 = 돈을 받은 달)에 화살표.
  //   큰 글씨 「8월」 줄(6월에 번 돈)을 「8월 수익」으로 착각해 올리던 것(레디꼬드라마·무드리)을 막는다.
  const rows = [m - 2, m - 1, m].map((mm) => {
    const d = new Date(Date.UTC(parseInt(y, 10), mm - 1, 1));
    const naverMonth = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 2, 1));
    return {
      pay: `${naverMonth.getUTCMonth() + 1}월`,
      payNum: naverMonth.getUTCMonth() + 1,
      agg: `${d.getUTCFullYear()}. ${d.getUTCMonth() + 1}.`,
      hot: mm === m,
    };
  });
  return (
    <Box bg="neutral.50" border="1px solid" borderColor="neutral.200" borderRadius="10px" p={3} fontFamily="Pretendard, sans-serif" userSelect="none" aria-hidden>
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontSize="sm" fontWeight="800" color="neutral.900">{CAPTURE_GUIDE.title} <Box as="span" color="neutral.400" fontSize="xs">ⓘ</Box></Text>
        <Box fontSize="xs" border="1px solid" borderColor="neutral.300" borderRadius="6px" px={2} py={0.5} bg="white" color="neutral.700">{y} ▾</Box>
      </Flex>
      <Box border="2px solid" borderColor="status.error" borderRadius="8px" bg="white" overflow="hidden">
        <Flex justify="space-between" px={3} py={1.5} fontSize="xs" fontWeight="700" color="neutral.700" borderBottom="1px solid" borderColor="neutral.200">
          <Text>{CAPTURE_GUIDE.columns[0]}</Text>
          <Text>{CAPTURE_GUIDE.columns[1]}</Text>
        </Flex>
        {rows.map((r) => (
          <Flex key={r.agg} justify="space-between" align="center" px={3} py={2} bg={r.hot ? "brand.50" : "white"} opacity={r.hot ? 1 : 0.55} borderBottom="1px solid" borderColor="neutral.100" outline={r.hot ? "2px solid" : undefined} outlineColor="brand.500" outlineOffset="-2px" data-hot={r.hot ? "1" : undefined}>
            <Box>
              <Text fontSize="sm" fontWeight="800" color="neutral.900">
                {r.pay}
                {r.hot && <Box as="span" ml={1.5} fontSize="xs" color="brand.700">{tt("a.nv14g01", "← 이 줄")}</Box>}
              </Text>
              <Text fontSize="xs" color="neutral.500">
                {r.agg}
                {r.hot && <Box as="span" ml={1} color="brand.700" fontWeight="700">{tt("a.nv14g02", "← {{v0}}월에 번 돈", { v0: m })}</Box>}
              </Text>
            </Box>
            <Box textAlign="right">
              <Text fontSize="sm" fontWeight="800" color="neutral.900">{tt("a.c5d4b351", "●●●,●●●원")}</Text>
              <Text fontSize="xs" color="neutral.500">{CAPTURE_GUIDE.statusLabel}</Text>
            </Box>
          </Flex>
        ))}
      </Box>
      <Text fontSize="xs" fontWeight="700" color="status.error" mt={2}>
        {tt("a.7942e7b9", "이 화면처럼 캡처해 주세요")}
      </Text>
      {highlightMonth && (
        <Text fontSize="xs" color="neutral.600">
          {tt("a.nv14g03", "네이버는 돈을 받은 달을 크게 써요. {{v0}}월에 번 돈은 {{v1}}월에 받으니 「{{v1}}월」 줄이에요.", { v0: m, v1: rows[2].payNum })}
        </Text>
      )}
      {/* 2026-10-08 — 확정 전 화면(지급 예정·산정 중)은 접수되지 않는다는 것을 그림 바로 아래에서 말한다 */}
      <Text fontSize="xs" fontWeight="700" color="accent.700" mt={1.5} data-testid="naver-capture-not-final">
        {CAPTURE_GUIDE.notFinalNote}
      </Text>
    </Box>
  );
}

/**
 * 5만원 미만 이월 달 그림(2026-10-13) — 네이버 앱 「수익」 화면 위쪽의 「최소 수익 5만원 미만」 카드 + 월별 수익 그래프 그 달 툴팁.
 * 크리에이터들이 실제로 올린 캡처의 배치를 따라 그렸다(금액은 ●●●). 이 두 가지가 한 화면에 있어야 자동 읽기가 이월 달로 받는다.
 * 2026-10-14(제이 확인) — 제이가 보낸 화면 그대로: 카드 세 줄 · 「월별 수익 현황」 기간 칩 · 여섯 달 막대 · 그 달 막대를 눌러 뜬 금액 말풍선.
 */
const CARRIED_BARS = [30, 26, 78, 6, 4, 0]; // 그 달(넷째) 앞뒤 여섯 달 — 그림일 뿐, 실제 금액이 아니다
const CARRIED_AT = 3;
export function NaverCarriedSample({ revenueMonth }) {
  const rm = /^\d{6}$/.test(String(revenueMonth || "")) ? String(revenueMonth) : "202608";
  const months = CARRIED_BARS.map((_, i) => shiftMonth(rm, i - CARRIED_AT));
  const dot = (ym) => `${ym.slice(0, 4)}. ${parseInt(ym.slice(4, 6), 10)}.`;
  return (
    <Box bg="neutral.50" border="1px solid" borderColor="neutral.200" borderRadius="10px" p={3} fontFamily="Pretendard, sans-serif" userSelect="none" aria-hidden data-testid="naver-carried-sample">
      <Box border="2px solid" borderColor="status.error" borderRadius="8px" bg="white" p={2.5}>
        <Box border="1px solid" borderColor="neutral.200" borderRadius="8px" px={2.5} py={2} mb={2}>
          <Flex align="center" gap={1.5}>
            <Flex w="14px" h="14px" borderRadius="full" bg="status.error" color="white" fontSize="9px" fontWeight="800" align="center" justify="center">!</Flex>
            <Text fontSize="sm" fontWeight="800" color="neutral.900">{tt("a.nv13g01", "최소 수익 5만원 미만")}</Text>
          </Flex>
          <Text fontSize="xs" color="neutral.600">{tt("a.nv13g02", "수익 자동 이월 처리")}</Text>
          <Text fontSize="10px" color="neutral.500" lineHeight="1.5">{tt("a.nv15g01", "최소 수익 기준을 충족하지 않아 수익이 자동 이월되었습니다.")}</Text>
        </Box>
        <Flex justify="space-between" align="center" gap={2} mb={1}>
          <Text fontSize="sm" fontWeight="800" color="neutral.900">{tt("a.nv13g03", "월별 수익 현황")}</Text>
          <Box fontSize="10px" border="1px solid" borderColor="neutral.300" borderRadius="6px" px={1.5} py={0.5} color="neutral.700" whiteSpace="nowrap">
            {dot(months[0])} ~ {dot(months[months.length - 1])} ▾
          </Box>
        </Flex>
        {/* 위 34px 는 말풍선 자리 — 그 달 막대를 눌렀을 때 뜨는 금액. 막대만 찍고 금액이 안 보이면 금액을 읽을 수 없다 */}
        <Box position="relative" h="96px" pt="34px">
          <Box
            position="absolute" top={0} left={`${((CARRIED_AT + 0.5) / CARRIED_BARS.length) * 100}%`} transform="translateX(-50%)"
            bg="white" border="1px solid" borderColor="neutral.300" borderRadius="6px" px={2} py={0.5} boxShadow="sm" zIndex={1} whiteSpace="nowrap" textAlign="center"
            data-testid="naver-carried-tip"
          >
            <Text fontSize="10px" color="neutral.500">{dot(rm)}</Text>
            <Text fontSize="xs" fontWeight="800" color="neutral.900">{tt("a.nv15g02", "●●,●●●원")}</Text>
          </Box>
          <Flex h="100%" align="flex-end" gap={2} px={1} borderBottom="1px solid" borderColor="neutral.200">
            {CARRIED_BARS.map((h, i) => (
              <Box key={months[i]} flex={1} h={`${h}%`} minH={h ? "3px" : 0} bg={i === CARRIED_AT ? "green.500" : "green.200"} borderTopRadius="2px" />
            ))}
          </Flex>
        </Box>
        <Flex gap={2} px={1} mt={1}>
          {months.map((ym, i) => (
            <Text key={ym} flex={1} textAlign="center" fontSize="9px" color={i === CARRIED_AT ? "neutral.900" : "neutral.500"} fontWeight={i === CARRIED_AT ? "800" : "400"}>
              {`${parseInt(ym.slice(4, 6), 10)}월`}
            </Text>
          ))}
        </Flex>
      </Box>
      <Text fontSize="xs" fontWeight="700" color="status.error" mt={2}>
        {revenueMonth
          ? tt("a.nv15g03", "그래프에서 {{v0}} 막대를 눌러 금액 말풍선이 뜨게 한 뒤, 위쪽 안내 카드와 함께 한 화면에 찍어 주세요", { v0: monthLabel(revenueMonth) })
          : tt("a.nv13g05", "위쪽 안내 카드와 그 달 막대를 눌렀을 때 뜨는 금액이 한 화면에 보이게 찍어 주세요")}
      </Text>
    </Box>
  );
}

/** 경로 + 그림 + 규칙 3줄. 시트 안내 단계와 가이드 문서에서 같은 모양으로 쓴다 */
export default function NaverCaptureGuide({ revenueMonth, compact = false }) {
  return (
    <Box>
      <Flex wrap="wrap" align="center" gap={1} fontSize="xs" color="neutral.700" mb={2}>
        {CAPTURE_GUIDE.path.map((p, i) => (
          <React.Fragment key={p}>
            {i > 0 && <Box as="span" color="neutral.400">›</Box>}
            <Box as="span" bg="neutral.100" borderRadius="4px" px={1.5} py={0.5} fontWeight={i === CAPTURE_GUIDE.path.length - 1 ? "800" : "600"}>{p}</Box>
          </React.Fragment>
        ))}
      </Flex>
      <NaverSettlementSample highlightMonth={revenueMonth} />
      {!compact && (
        <Box as="ul" pl={4} mt={2} fontSize="xs" color="neutral.700" lineHeight="1.7">
          {CAPTURE_GUIDE.rules.map((r) => <li key={r}>{r}</li>)}
          <li>{CAPTURE_GUIDE.carriedNote}</li>
        </Box>
      )}
    </Box>
  );
}

export { NaverSettlementSample };
